import type { NavigationGuard, RouteRecordRaw, RouteRecordRedirectOption } from 'vue-router'

import { keys } from '@/app/i18n'
import { ROUTE_NOT_AUTHORIZED } from '@/app/router/names'
import { useAuthStore } from '@/app/stores/auth'

/**
 * The tasks module's routes (WP-03, wp/tasks §3). Paths and names from the route manifest (D-15); the legacy paths
 * (`/:software/task/view/:id`, `/:software/edit-tasks/:id`, …) redirect here through
 * `src/app/router/legacy-redirects.ts` — the task e-mails' `/en/task/view/{id}` included (OPEN-10: no redirect to the
 * user's software).
 *
 * NEW-14: legacy routes carried `meta.module` / `mode` that nothing enforced. The guard checks `resource` / `action`
 * (D-21); the forms also refuse Freelancers (they have no nav entry for them and the API answers 403), as `iCan` let
 * every Freelancer through.
 *
 * Task templates and task groups were dropped (2026-10-29, owner's call, as in Documents): their paths redirect to the
 * list. The API, the tables and the tasks' group links are untouched, so the decision can be undone.
 */
const noFreelancer: NavigationGuard = () =>
  useAuthStore().userType === 'Freelancer' ? { name: ROUTE_NOT_AUTHORIZED } : true

// "Tasks" alone leads to the list: the legacy "Task list" crumb in between said the same twice (2026-10-30, as Documents).
const tasksCrumb = { text: keys.t('common.nav.tasks'), to: { name: 'tasks' } }
const active = (text: string) => ({ text, active: true })

const TaskEditView = () => import('./views/TaskEditView.vue')
/** The task form mounts `DocumentPicker` (`src/shared/documents`), whose strings are `documents.*` (queue #8). */
const formCatalogues = ['documents']
const TaskRecordView = () => import('./views/TaskRecordView.vue')

/** Where the dropped template and group paths land: the task list of the same software. */
const toTasks: RouteRecordRedirectOption = (to) => ({ name: 'tasks', params: { software: to.params.software } })

const routes: RouteRecordRaw[] = [
  {
    path: '/:software/tasks',
    name: 'tasks',
    component: () => import('./views/TaskListView.vue'),
    meta: {
      software: 'common',
      resource: 'tasks',
      pageTitle: keys.t('common.nav.tasks'),
      breadcrumb: [active(keys.t('common.nav.tasks'))],
    },
  },
  {
    path: '/:software/tasks/new',
    name: 'create-task',
    component: TaskEditView,
    beforeEnter: noFreelancer,
    meta: {
      catalogues: formCatalogues,
      software: 'common',
      resource: 'tasks',
      action: 'write',
      navActiveLink: 'tasks',
      pageTitle: keys.t('tasks.edit.title.createTask'),
      breadcrumb: [tasksCrumb, active(keys.t('tasks.edit.title.task'))],
    },
  },
  {
    path: '/:software/tasks/:id(\\d+)',
    name: 'task-view',
    component: TaskRecordView,
    meta: {
      software: 'common',
      resource: 'tasks',
      navActiveLink: 'tasks',
      pageTitle: keys.t('tasks.detail.title.viewTask'),
      breadcrumb: [tasksCrumb, active(keys.t('tasks.edit.title.task'))],
    },
  },
  {
    path: '/:software/tasks/:id(\\d+)/edit',
    name: 'edit-tasks',
    component: TaskEditView,
    beforeEnter: noFreelancer,
    meta: {
      catalogues: formCatalogues,
      software: 'common',
      resource: 'tasks',
      action: 'write',
      navActiveLink: 'tasks',
      pageTitle: keys.t('tasks.edit.title.editTask'),
      breadcrumb: [tasksCrumb, active(keys.t('tasks.edit.title.task'))],
    },
  },
  // the dropped templates and groups (the manifest's paths; "new task from template" lands on the list too)
  { path: '/:software/tasks/new/:templateId(\\d+)', redirect: toTasks },
  { path: '/:software/tasks/templates', name: 'task-templates', redirect: toTasks },
  { path: '/:software/tasks/templates/new', name: 'create-task-template', redirect: toTasks },
  { path: '/:software/tasks/templates/:id(\\d+)', name: 'task-template-view', redirect: toTasks },
  { path: '/:software/tasks/templates/:id(\\d+)/edit', name: 'edit-task-template', redirect: toTasks },
  { path: '/:software/task-groups', name: 'task-groups', redirect: toTasks },
  { path: '/:software/task-groups/new', name: 'create-task-group', redirect: toTasks },
  { path: '/:software/task-groups/:id(\\d+)/edit', name: 'edit-task-group', redirect: toTasks },
]

export default routes
